import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { ActionText, Column, Dialog, DialogFooter, Text } from "@mdotm/shadcn/legacy/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";

export const thirdPartyProviderTermsUrl = "https://www.refinitiv.com/en/policies/third-party-provider-terms";

/** The terms Refinitiv requires for Lipper funds data, from the "Disclaimer per Clienti che usano dati Lipper" doc. */
function LipperTerms() {
	return (
		<Column gap={12}>
			<Text type="Body/M/Book" as="p">
				Sphere may incorporate data and information supplied by third-party data providers. Certain providers require
				specific terms, notices and disclaimers to apply to the use of their data. The applicable provider-specific
				terms are set out below.
			</Text>
			<Text type="Body/M/Book" as="p">
				The Refinitiv entity and its Affiliates are referred to below as &quot;Refinitiv&quot;.
			</Text>
			<Text type="Body/M/Book" as="p">
				The &quot;Information Product&quot; is any data or service provided by Refinitiv. Refinitiv or its third party
				providers own and retain all rights, title and interest, including but not limited to copyright, trademarks,
				patents, database rights, trade secrets, know-how, and all other intellectual property rights or forms of
				protection of similar nature or having equivalent effect, anywhere in the world, in the Information Product and
				user is not granted any proprietary interest therein or thereto. The Information Product constitutes
				confidential and trade secrets of Refinitiv or its third party providers. Display, performance, reproduction,
				distribution of, or creation of derivative works or improvements from Information Product in any form or manner
				is expressly prohibited, except to the extent expressly permitted hereunder, or otherwise, with the prior
				written permission of Refinitiv.
			</Text>
			<Text type="Body/M/Book" as="p">
				User may use the Information Product for internal purposes only. User may copy, paste and distribute internally
				only an insubstantial amount of the data contained in the Information Product provided that: (a) the
				distribution is incidental to or supports user&apos;s business purpose; (b) the data is not distributed by user
				in connection with information vending or commercial publishing (in any manner or format whatsoever), not
				reproduced through the press or mass media or on the Internet; and (c) where practicable, clearly identifies
				Refinitiv or its third party providers as the source of the data. Data will be considered in &quot;insubstantial
				amount&quot; if such amount (a) has no independent commercial value; or (b) could not be used by the recipient
				as a substitute for any product or service (including any download service) provided by Refinitiv or a
				substantial part of it.
			</Text>
			<Text type="Body/M/Book" as="p">
				To the extent that the Information Product contains any third party data referred to in the General
				Restrictions/Notices page set forth on{" "}
				<ActionText as="a" href={thirdPartyProviderTermsUrl} target="_blank" rel="noopener noreferrer">
					{thirdPartyProviderTermsUrl}
				</ActionText>
				, the terms set forth on such General Restrictions/Notices page shall apply to user.
			</Text>
			<Text type="Body/M/Book" as="p">
				User acknowledges that access to certain elements of the Information Product may cease or may be made subject to
				certain conditions by Refinitiv or upon the instructions of the third party provider of those elements. Upon
				termination or expiration of this user license, all rights granted hereunder shall immediately terminate and
				user shall cease to use the Information Product and delete or destroy all copies thereof in its possession or
				control.
			</Text>
			<Text type="Body/M/Book" as="p">
				NEITHER REFINITIV NOR ITS THIRD PARTY PROVIDERS WARRANT THAT THE PROVISION OF THE INFORMATION PRODUCT WILL BE
				UNINTERRUPTED, ERROR FREE, TIMELY, COMPLETE OR ACCURATE, NOR DO THEY MAKE ANY WARRANTIES AS TO THE RESULTS TO BE
				OBTAINED FROM USE OF THE SAME. USE OF THE INFORMATION PRODUCT AND RELIANCE THEREON IS AT USER&apos;S SOLE RISK.
				NEITHER REFINITIV OR ITS THIRD PARTY PROVIDERS WILL IN ANY WAY BE LIABLE TO USER OR ANY OTHER ENTITY OR PERSON
				FOR THEIR INABILITY TO USE THE INFORMATION PRODUCT, OR FOR ANY INACCURACIES, ERRORS, OMISSIONS, DELAYS, COMPUTER
				VIRUS OR OTHER INFIRMITY OR CORRUPTION, DAMAGES, CLAIMS, LIABILITIES OR LOSSES, REGARDLESS OF CAUSE, IN OR
				ARISING FROM THE USE OF THE INFORMATION PRODUCT. THE INFORMATION PRODUCT IS PROVIDED ON AN &quot;AS IS&quot;
				BASIS AND WITHOUT WARRANTY OF ANY KIND. NO WARRANTIES EITHER EXPRESSED OR IMPLIED, INCLUDING BUT NOT LIMITED TO
				ANY IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, INFRINGEMENT OR OTHERWISE IS
				PROVIDED HEREUNDER.
			</Text>
			<Text type="Body/M/Book" as="p">
				IN NO EVENT WILL REFINITIV OR ITS THIRD PARTY PROVIDERS BE LIABLE FOR ANY DAMAGES, INCLUDING WITHOUT LIMITATION
				DIRECT OR INDIRECT, SPECIAL, INCIDENTAL, OR CONSEQUENTIAL DAMAGES, LOSSES OR EXPENSES ARISING IN CONNECTION WITH
				INFORMATION PRODUCT EVEN IF REFINITIV OR ITS THIRD PARTY PROVIDERS OR THEIR REPRESENTATIVES ARE ADVISED OF THE
				POSSIBILITY OF SUCH DAMAGES, LOSSES OR EXPENSES. FURTHER, REFINITIV OR ITS THIRD PARTY PROVIDERS SHALL NOT BE
				LIABLE IN ANY MANNER FOR REDISTRIBUTOR&apos;S PRODUCTS OR SERVICES.
			</Text>
		</Column>
	);
}

function LipperTermsDialog({ show, onClose, onAnimationStateChange }: MinimumDialogProps) {
	return (
		<Dialog
			show={show}
			onClose={onClose}
			onAnimationStateChange={onAnimationStateChange}
			header="Terms & Conditions"
			size="large"
			data-qualifier="AppDrawer/LipperTermsDialog"
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose} data-qualifier="AppDrawer/LipperTermsDialog/Close">
							Close
						</Button>
					}
				/>
			}
		>
			<LipperTerms />
		</Dialog>
	);
}

export function spawnLipperTermsDialog(): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<LipperTermsDialog
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
			/>
		)),
	);
}
